/**
 * CriterionRow — one AI Review scoring criterion (v38 requirements-analysis
 * R2, `#aiCriteriaList` line 1749): a free-text prompt and a remove button.
 *
 * All criteria are equal weight — there is no per-row weight mechanic here
 * (no slider, no number input, no normalized-% label); the Total column
 * (`ScoresTable`) is always the simple average across criteria.
 *
 * `readOnly` renders the SAME row with the SAME field, marked `readOnly` (and
 * `aria-readonly`), and no remove button. Trello #711 cl.5 asks for the
 * criteria of a finished review to be shown "read-only with the same component
 * as the form", so the detail page and the creator cannot drift apart — a
 * criterion the form can express is a criterion the file can display, by
 * construction. It used to swap the `<input>` for a `<span>`, which remounted
 * the field on every toggle (focus, caret and undo gone) and gave assistive
 * tech a nameless run of text; the field keeps its label in both modes (PRD
 * `2026-10-06-ai-review-context-prompt-viewer`, C2).
 *
 * The field is a `<textarea>` because one element has to WRAP a long
 * criterion (cl.4's "no truncation ever") and an `<input>` cannot. It is kept
 * a one-line value the way an input is: Enter inserts nothing and a pasted
 * line break becomes a space. It grows with its content through
 * `field-sizing: content`; a browser without it shows one line and scrolls.
 */
import { X } from "lucide-react";
import { useLayoutEffect, useRef } from "react";

/**
 * A DISCRIMINATED UNION on `readOnly`, mirroring `CriteriaPanel`'s.
 *
 * The props were three independent optionals until `/pr-review` round 8 (§2c)
 * pointed out that this re-opened, one level down, the exact hole the panel's
 * union was added to close: `<CriterionRow id text />` type-checked and
 * rendered an editable input whose every keystroke went to `onTextChange?.()`
 * and vanished. A silently inert form field is the worst shape of that bug —
 * it looks like it works.
 *
 * Making both branches explicit means the compiler asks the question: an
 * editable row MUST be handed the two handlers; a read-only one must not be
 * handed either.
 */
interface EditableCriterionRowProps {
  id: string;
  text: string;
  readOnly?: false;
  /** 1-based position: names the remove button ("Remove criterion 2"), so
   *  eight buttons are not eight identical "Remove criterion"s. */
  position: number;
  onTextChange: (id: string, text: string) => void;
  onRemove: (id: string) => void;
}

interface ReadOnlyCriterionRowProps {
  id: string;
  text: string;
  readOnly: true;
  /** 1-based position in the list. Read-only rows are tab stops a screen
   *  reader walks one by one; "Criterion 2 description" tells them apart where
   *  eight identical "Criterion description" names would not. */
  position: number;
  onTextChange?: never;
  onRemove?: never;
}

export type CriterionRowProps = EditableCriterionRowProps | ReadOnlyCriterionRowProps;

/** Criterion description max length — mirrors the backend's field limit. */
const MAX_TEXT_LENGTH = 100;

/** A criterion is one line: a pasted line break becomes a space. */
function toOneLine(value: string): string {
  return value.replace(/\r\n|\r|\n/g, " ");
}

/** Shared by both modes. No `truncate`: a long criterion wraps. */
const FIELD =
  "min-w-0 flex-1 resize-none whitespace-pre-wrap break-words [field-sizing:content] text-body text-text";
/** The editable field: a bordered input inside the row's box. */
const EDITABLE =
  "rounded-sm border border-border bg-bg px-2.5 py-1.5 placeholder:text-text-muted hover:border-border-hover focus:border-border-hover focus:outline-none";
/** Read-only: the text sits in the row's box with no field chrome — the look
 *  the static rendering had (Lucas, 2026-08-26). It stays focusable, so a
 *  keyboard focus is still shown. */
const READ_ONLY =
  "rounded-xs bg-transparent p-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent";

/** `field-sizing: content` is what grows the field; Firefox lacks it. */
function supportsFieldSizing(): boolean {
  return typeof CSS !== "undefined" && typeof CSS.supports === "function"
    ? CSS.supports("field-sizing", "content")
    : false;
}

export function CriterionRow({
  id,
  text,
  onTextChange,
  onRemove,
  readOnly = false,
  position,
}: CriterionRowProps) {
  const fieldRef = useRef<HTMLTextAreaElement>(null);
  // FALLBACK autosize for browsers without `field-sizing: content`: without it
  // a `rows={1}` textarea shows one scrolling line, which truncates a long
  // read-only criterion (cl.4). A computed height, written through the ref —
  // the same class of exception as DockablePanel's positional transform, not
  // static styling. `text` is the trigger: it is what changes the content.
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-measure when the text changes
  useLayoutEffect(() => {
    const field = fieldRef.current;
    if (!field || supportsFieldSizing()) return;
    field.style.height = "auto";
    // Not laid out (hidden, or no layout engine): leave the natural height.
    if (field.scrollHeight === 0) return;
    // scrollHeight excludes the border; border-box sizing needs it back.
    const border = field.offsetHeight - field.clientHeight;
    field.style.height = `${field.scrollHeight + border}px`;
  }, [text]);

  // THE SAME BOX in both modes (Lucas, 2026-08-26). A flat bullet list was
  // tried and rejected: the recruiter should recognise the criteria in the
  // shape they typed them on the AI Review screen, and the box is what makes
  // one criterion read as one field rather than as a line of a paragraph.
  return (
    <div
      className="flex max-w-xl items-center gap-3 rounded-md border border-border bg-surface px-3 py-2"
      data-testid={`criterion-row-${id}`}
    >
      <textarea
        ref={fieldRef}
        rows={1}
        value={text}
        maxLength={MAX_TEXT_LENGTH}
        readOnly={readOnly}
        aria-readonly={readOnly || undefined}
        onChange={(e) => onTextChange?.(id, toOneLine(e.target.value).slice(0, MAX_TEXT_LENGTH))}
        onKeyDown={(e) => {
          // An IME's Enter commits the composed word; only a plain Enter is
          // refused.
          if (e.key === "Enter" && !e.nativeEvent.isComposing) e.preventDefault();
        }}
        placeholder={readOnly ? undefined : "e.g. 10+ yrs backend leadership"}
        aria-label={readOnly ? `Criterion ${position} description` : "Criterion description"}
        data-testid={`criterion-text-${id}`}
        className={`${FIELD} ${readOnly ? READ_ONLY : EDITABLE}`}
      />
      {readOnly ? null : (
        <button
          type="button"
          onClick={() => onRemove?.(id)}
          aria-label={`Remove criterion ${position}`}
          data-testid={`criterion-remove-${id}`}
          className="shrink-0 rounded-sm p-1 text-text-muted hover:bg-error-muted hover:text-error"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      )}
    </div>
  );
}
